import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import Pagination from '../../../components/common/Pagination';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const WholesaleVendorsPage = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    is_active: 'true',
    vendor_type: ''
  });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

  useEffect(() => {
    fetchVendors();
  }, [filters, currentPage]);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      setError('');
      const params = { 
        ...filters, 
        page: currentPage, 
        limit: 20,
        module: 'wholesale' // Filter by wholesale module
      };
      if (params.is_active === 'all') delete params.is_active;

      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/vendors`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      setVendors(response.data?.data || []);
      setPagination(response.data?.pagination || { page: currentPage, limit: 20, total: response.data?.data?.length || 0, totalPages: 1 });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (vendorId) => {
    if (!window.confirm('Are you sure you want to deactivate this vendor?')) return;

    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API_URL}/vendors/${vendorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchVendors();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to deactivate vendor');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'wholesale_admin' || user?.role === 'system_admin';

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Wholesale Vendors</h1>
          <p className="text-sm text-gray-500">Manage your wholesale suppliers and vendors</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => navigate('/wholesale/accounting/vendors/new')}
            className="text-white px-4 py-2 rounded-lg"
            style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
          >
            + Add Vendor
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Search by vendor name..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.is_active}
              onChange={(e) => setFilters({ ...filters, is_active: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="all">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={filters.vendor_type}
              onChange={(e) => setFilters({ ...filters, vendor_type: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">All Types</option>
              <option value="tile_supplier">Tile Supplier</option>
              <option value="accessory_supplier">Accessory Supplier</option>
              <option value="service_provider">Service Provider</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Vendors Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--wholesale-primary, #047857)' }}></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor #</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Bills</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Outstanding</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {vendors.length > 0 ? (
                  vendors.map(vendor => (
                    <tr
                      key={vendor.id}
                      onClick={() => navigate(`/wholesale/accounting/vendors/${vendor.id}`)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: 'var(--wholesale-primary, #047857)' }}>
                        {vendor.vendor_code}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {vendor.vendor_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>{vendor.phone || '-'}</div>
                        <div className="text-xs text-gray-400">{vendor.email || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                        {vendor.vendor_type?.replace('_', ' ') || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {vendor.bill_count || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">
                        <span className={parseFloat(vendor.outstanding_balance) > 0 ? 'text-red-600' : 'text-gray-900'}>
                          {formatCurrency(vendor.outstanding_balance)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
                          vendor.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {vendor.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {isAdmin && vendor.is_active && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeactivate(vendor.id);
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            Deactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                      No wholesale vendors found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={(page) => setCurrentPage(page)}
          itemLabel="vendors"
        />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Total Vendors</div>
          <div className="text-2xl font-bold text-gray-900">{vendors.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Active Vendors</div>
          <div className="text-2xl font-bold text-green-600">
            {vendors.filter(v => v.is_active).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Total Outstanding</div>
          <div className="text-2xl font-bold text-red-600">
            {formatCurrency(vendors.reduce((sum, v) => sum + parseFloat(v.outstanding_balance || 0), 0))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WholesaleVendorsPage;
